import { BulbOutlined, DeleteOutlined, DownloadOutlined, SettingOutlined } from '@ant-design/icons';
import { Button, Checkbox, Dropdown, Pagination, Popconfirm, Select, Tooltip } from 'antd';
import blobStream from 'blob-stream';
import { saveAs } from 'file-saver';
import { groupBy } from 'lodash';
import { Dispatch, SetStateAction, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SVGtoPDF from 'svg-to-pdfkit';
import { getPageIDFromNumber } from './MelodyAnalysis';
import MusicSheet from './models/MusicSheet';
import { Pattern } from './models/Pattern';
import PDFDocument from './pdfDocument';
import { ThemeContext } from './ThemeContext';

export const musicSheetSelectionID = 'music-sheet-selection';

const { Option, OptGroup } = Select;

const AlignedPagination = styled(Pagination)`
  .ant-pagination-item-link {
    line-height: 0;
  }
`;

const MusicSheetSelection = ({
  currentPage,
  isMusicSheetRendered,
  musicSheets,
  numberOfPages,
  selectedMusicSheetID,
  setConfiguringID,
  setCurrentPage,
  setIsMusicSheetRendered,
  setNewPattern,
  setNumberOfPages,
  setPatterns,
  setSelectedMusicSheetID,
  setShowOnBoarding,
  setSuggestingID
}: {
  currentPage: number;
  isMusicSheetRendered: boolean;
  musicSheets: MusicSheet[];
  numberOfPages: number;
  selectedMusicSheetID: string;
  setConfiguringID: Dispatch<SetStateAction<string>>;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setIsMusicSheetRendered: Dispatch<SetStateAction<boolean>>;
  setNewPattern: Dispatch<SetStateAction<Pattern>>;
  setNumberOfPages: Dispatch<SetStateAction<number>>;
  setPatterns: Dispatch<SetStateAction<Pattern[]>>;
  setSelectedMusicSheetID: Dispatch<SetStateAction<string>>;
  setShowOnBoarding: Dispatch<SetStateAction<boolean>>;
  setSuggestingID: Dispatch<SetStateAction<string>>;
}) => {
  const [theme, setTheme] = useContext(ThemeContext);
  // The button's tooltip would cover the confirmation's buttons.
  const [isConfirmingDeletion, setIsConfirmingDeletion] = useState(false);
  const { t: translate } = useTranslation();

  return (
    <div
      className="flex flex-1 flex-wrap items-center justify-evenly gap-x-4 gap-y-2"
      id={musicSheetSelectionID}
    >
      <div className="min-w-0 max-w-full">
        <Select
          aria-label={translate('musicSheetSelection.label')}
          style={{ maxWidth: '100%', width: 350 }}
          // Styles the options in the list only, rc-select would also apply an option's own className to the value.
          classNames={{ popup: { listItem: 'mb-4 font-bold tracking-tight text-gray-900' } }}
          popupMatchSelectWidth={false}
          onChange={(value) => {
            setIsMusicSheetRendered(false);
            setNumberOfPages(undefined);

            setSelectedMusicSheetID(value);
          }}
          size="large"
          value={selectedMusicSheetID}
        >
          {Object.entries(groupBy(musicSheets, 'category')).map(
            ([category, musicSheetsInCategory]: [string, [MusicSheet]]) => (
              <OptGroup
                key={category}
                label={translate(`musicSheets.${category}`, { defaultValue: category })}
              >
                {musicSheetsInCategory.map((musicSheet) => (
                  <Option key={musicSheet.id} value={musicSheet.id}>
                    <b>
                      {translate('musicSheets.title', {
                        composer: musicSheet.composer,
                        title: translate(`musicSheets.${musicSheet.title}`, {
                          defaultValue: musicSheet.title
                        })
                      })}
                    </b>
                  </Option>
                ))}
              </OptGroup>
            )
          )}
        </Select>
      </div>
      <AlignedPagination
        current={currentPage}
        defaultPageSize={1}
        disabled={!isMusicSheetRendered}
        onChange={(pageNumber) => setCurrentPage(pageNumber)}
        size="small"
        total={numberOfPages}
      />
      <div className="flex items-center">
        {/* Icon buttons carry their names for screen readers and as tooltips (audit F08). */}
        <Tooltip title={translate('toolbar.tour')}>
          <Button
            aria-label={translate('toolbar.tour')}
            className="mr-2"
            icon={<BulbOutlined />}
            onClick={() => setShowOnBoarding(true)}
            shape="circle"
            size="small"
          />
        </Tooltip>
        <Tooltip title={translate('toolbar.exportPDF')}>
          <Button
            aria-label={translate('toolbar.exportPDF')}
            className="mr-2"
            icon={<DownloadOutlined />}
            onClick={() => {
              const selectedMusicSheet = musicSheets.find(
                (musicSheet) => musicSheet.id === selectedMusicSheetID
              );
              const pdfDocument = new PDFDocument({
                autoFirstPage: false,
                info: {
                  Title: selectedMusicSheet.title,
                  Subject: translate('melodyAnalysis')
                },
                size: 'A4'
              });
              const stream = pdfDocument.pipe(blobStream());

              for (let pageNumber = 1; pageNumber <= numberOfPages; pageNumber++) {
                const pageContent = document.querySelector(
                  getPageIDFromNumber(pageNumber)
                ) as SVGElement;

                pdfDocument.addPage();
                SVGtoPDF(pdfDocument, pageContent.outerHTML, 0, 0, {
                  preserveAspectRatio: 'xMidYMid meet'
                });
              }

              pdfDocument.end();
              stream.on('finish', function () {
                const blob = this.toBlob();

                saveAs(
                  blob,
                  `${translate(`musicSheets.${selectedMusicSheet.title}`, {
                    defaultValue: selectedMusicSheet.title
                  })}.pdf`
                );
              });
            }}
            size="small"
            shape="circle"
          />
        </Tooltip>
        {/* The analysis is saved in the browser, so deleting everything asks first (audit ISSUE-009). */}
        <Popconfirm
          cancelText={translate('toolbar.cancel')}
          okButtonProps={{ danger: true }}
          okText={translate('toolbar.delete')}
          onOpenChange={setIsConfirmingDeletion}
          onConfirm={() => {
            setConfiguringID(undefined);
            setNewPattern(undefined);
            setPatterns([]);
            setSuggestingID(undefined);
          }}
          title={translate('toolbar.deleteAllConfirm')}
        >
          <Tooltip
            open={isConfirmingDeletion ? false : undefined}
            title={translate('toolbar.deleteAll')}
          >
            <Button
              aria-label={translate('toolbar.deleteAll')}
              className="mr-2"
              danger
              icon={<DeleteOutlined />}
              size="small"
              shape="circle"
            />
          </Tooltip>
        </Popconfirm>
        <Dropdown
          menu={{
            items: [
              {
                key: 'color-by-voice',
                label: (
                  <Checkbox
                    checked={theme.colorByVoice}
                    onChange={(event) =>
                      setTheme((oldTheme) => ({
                        ...oldTheme,
                        colorByVoice: event.target.checked,
                        colorVoices: oldTheme.colorVoices
                      }))
                    }
                  >
                    {translate('settings.colorByVoice')}
                  </Checkbox>
                )
              },
              {
                key: 'color-voices',
                label: (
                  <Checkbox
                    checked={theme.colorVoices}
                    onChange={(event) =>
                      setTheme((oldTheme) => ({
                        ...oldTheme,
                        colorByVoice: oldTheme.colorByVoice,
                        colorVoices: event.target.checked
                      }))
                    }
                  >
                    {translate('settings.colorVoices')}
                  </Checkbox>
                )
              }
            ]
          }}
          placement="bottomRight"
          trigger={['click']}
        >
          <Button
            aria-label={translate('toolbar.settings')}
            icon={<SettingOutlined />}
            size="small"
            shape="circle"
            title={translate('toolbar.settings')}
          />
        </Dropdown>
      </div>
    </div>
  );
};

export default MusicSheetSelection;
